import React from 'react';
import { Building2, Target, Hammer } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import developmentIcon from '@/assets/development-icon.jpg';
import managementIcon from '@/assets/management-icon.jpg';
import constructionIcon from '@/assets/construction-icon.jpg';

const Services = () => {
  const services = [
    {
      title: "Razvoj nepremičnin",
      description: "Kupujemo, prenavljamo in pozidamo nepremičnine. Od pridobitve dovoljenj do končne prodaje - celotni proces razvoja nepremičnin.",
      icon: Building2,
      image: developmentIcon,
      features: [
        "Iskanje in nakup primernih nepremičnin",
        "Arhitekturno načrtovanje in pridobitve dovoljenj",
        "Celovite prenove in pozidave",
        "Prodaja izboljšanih nepremičnin"
      ]
    },
    {
      title: "Investicijsko vodenje",
      description: "Strokovno vodimo investicijske projekte na področju nepremičnin in gradbeništva. Zagotavljamo optimalne rezultate vaših naložb.",
      icon: Target,
      image: managementIcon,
      features: [
        "Analiza investicijskih priložnosti",
        "Operativno vodenje projektov",
        "Nadzor nad stroški in časovnico",
        "Maksimiziranje donosa investicij"
      ]
    },
    {
      title: "Gradbena dela",
      description: "Butična storitev izvajanja gradbenih del. Specializirani smo za lastne potrebe, izbrane projekte pa izvajamo tudi za druge stranke.",
      icon: Hammer,
      image: constructionIcon,
      features: [
        "Ekskluzivna gradbena dela",
        "Visoka kakovost izvedbe",
        "Uporaba premium materialov",
        "Skrb za vsak detajl"
      ]
    }
  ];

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="storitve" className="py-20 bg-gradient-subtle">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in-up">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Naše <span className="text-atren-orange">storitve</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Ponujamo celovite rešitve na področju nepremičnin - od razvoja preko investicijskega 
            vodenja do vrhunskih gradbenih storitev.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <Card 
              key={index} 
              className="group hover:shadow-elegant transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm animate-scale-in overflow-hidden"
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              {/* Service Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-atren-gray/20 group-hover:bg-atren-orange/20 transition-colors duration-300"></div>
                <div className="absolute top-4 left-4 w-12 h-12 bg-white/90 rounded-lg flex items-center justify-center">
                  <service.icon className="w-6 h-6 text-atren-orange" />
                </div>
              </div>

              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-atren-orange transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  {service.description}
                </p>
                
                {/* Features List */}
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <div className="w-1.5 h-1.5 bg-atren-orange rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  variant="outline"
                  onClick={() => scrollToSection('#kontakt')}
                  className="w-full group-hover:bg-atren-orange group-hover:text-white group-hover:border-atren-orange transition-all duration-300"
                >
                  Povpraševanje
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16 animate-fade-in-up">
          <p className="text-lg text-muted-foreground mb-6">
            Imate specifične potrebe ali vprašanja o naših storitvah?
          </p>
          <Button
            size="lg"
            onClick={() => scrollToSection('#kontakt')}
            className="bg-gradient-primary shadow-primary hover:shadow-elegant transition-all duration-300"
          >
            Stopite v stik z nami
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Services;